'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { CompleteIcon, PlayVideoIcon, PausePlayerIcon } from '../Icons/Icons';
import { useState, useMemo } from 'react';
import { getLessonTypeLabel, getLessonDurationDisplay } from '@/lib/utils/lesson-meta';
import Bookmark from '../common/bookmark';
import { Skeleton } from '@/components/ui/skeleton';
import ItemsListSkeleton from '../skeletons/items-list';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { Lesson, UserCourse } from '@/types/user-course-lessons';
import { Course, CourseLesson } from '@/types/course';
import { sanitizeProgress } from '@/helpers/santinize-progress';

interface LessonsDataProps {
  courseUid: string;
  courseUrl: string;
  eyebrow: string;
  heading: string;
  /** When false, course dropdown starts collapsed. Default true. */
  defaultExpanded?: boolean;
  lessons: {
    chapter_heading: string;
    lessons: CourseLesson[];
  };
  lessonUrl?: string;
  userCourseData?: UserCourse | null;
  isLoading?: boolean;
  learningPath?: string;
  showAssessment?: boolean;
  isVideoPlaying?: boolean;
  currentLessonUid?: string;
  videoControls?: { play: () => void; pause: () => void } | null;
}

export default function LessonsSidebar({
  courseUid,
  courseUrl,
  lessonUrl,
  lessons,
  eyebrow,
  heading,
  defaultExpanded = true,
  userCourseData,
  isLoading = false,
  learningPath,
  showAssessment = false,
  isVideoPlaying = false,
  currentLessonUid,
  videoControls,
}: LessonsDataProps) {
  const router = useRouter();
  const [showMore, setShowMore] = useState<boolean>(defaultExpanded);
  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator) —
  // a useState seeded once from isUserLoggedIn() only reflected auth state as of mount.
  const { isLoggedIn } = useAuthUser();

  // Handle play/pause button clicks
  const handlePlayPauseClick = (
    e: React.MouseEvent,
    lessonUid?: string,
    lessonUrlPath?: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // If clicking on a different lesson, navigate to it
    if (lessonUid !== currentLessonUid) {
      if (lessonUrlPath) {
        router.push(`${courseUrl}${lessonUrlPath}`);
      }
      return;
    }

    // If it's the current lesson, control the video
    if (videoControls) {
      if (isVideoPlaying) {
        videoControls.pause();
      } else {
        videoControls.play();
      }
    }
  };

  // Derive data from userCourseData when logged in and data is available.
  // Gate on the mount-safe `isLoggedIn` state (false on server + first client
  // render) so server HTML matches the first client render — avoids hydration
  // mismatch. User data is swapped in after mount.
  const data = useMemo(() => {
    if (isLoggedIn && userCourseData) {
      return userCourseData;
    }
    // Fall back to CMS lessons data when not logged in or data not yet loaded
    return lessons as unknown as Course;
  }, [isLoggedIn, userCourseData, lessons]);

  return (
    <>
      <div className="w-full">
        <div className="bg-gradient-sheen py-4 ps-[22px] pe-6">
          <h6 className="cs-h6 text-[var(--Text-Default-Light)]">{eyebrow}</h6>
          <h4 className="cs-h4 mb-2 text-[var(--Text-Default-Medium)]">
            {lessons.chapter_heading || heading}
          </h4>
          {isLoggedIn && (
            <>
              <div className="bg-surface-5 h-1 rounded-full">
                <div
                  className={`h-1 rounded-full transition-all duration-300 ${
                    Number(sanitizeProgress(data?.progress?.percentage ?? 0)) === 100
                      ? 'bg-[var(--Color-Extended-Green-Green-Shade)]'
                      : 'bg-[var(--Color-Extended-Amethyst-Amethyst-Accent)]'
                  }`}
                  style={{
                    width: `${sanitizeProgress(data?.progress?.percentage ?? 0)}%`,
                  }}
                ></div>
              </div>
              <div className="cs-caption mt-1">
                {isLoading ? (
                  <Skeleton className="w-inherit h-4" />
                ) : (
                  <div className="">
                    {eyebrow}:{' '}
                    <span className="text-[var(--Color-Extended-Amethyst-Amethyst-Accent)]">
                      {sanitizeProgress(data?.progress?.percentage ?? 0)}%{' '}
                    </span>{' '}
                    Complete
                    <button
                      className="cs-caption text-text-action-medium hover:text-text-action-strong float-right cursor-pointer border-0 bg-transparent p-0"
                      onClick={() => setShowMore(!showMore)}
                    >
                      {showMore ? 'Show less' : 'Show more'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {showMore && (
          <div
            className={`max-h-[500px] w-full overflow-y-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${showMore ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'} `}
          >
            <ul>
              {isLoggedIn && isLoading ? (
                <ItemsListSkeleton />
              ) : (
                data?.lessons?.map((item: Lesson, index: number) => {
                  const duration = getLessonDurationDisplay(item);
                  return (
                    <li
                      key={item.entryUid || item.title || index}
                      className={`group relative w-full hover:bg-[var(--Surfaces-Extended-Amethyst-Transparent-Soft)] px-2 pe-0 lg:px-6 py-3 ${
                        item.url === lessonUrl ? 'bg-gradient-tile-sheen' : 'bg-surface-1'
                      }`}
                    >
                      <div className="flex flex-row items-center justify-between gap-4">
                        <Link
                          href={`${courseUrl}${item.url}`}
                          key={item.title}
                          prefetch={true}
                          className="w-[80%] cursor-pointer"
                        >
                          <h6 className="cs-h6 text-text-default-light">{item.title}</h6>
                          <div className="flex flex-row items-center gap-2">
                            <div className="cs-caption capitalize">
                              {getLessonTypeLabel(item)}
                            </div>
                            {duration ? (
                              <>
                                <div>
                                  <span className="divider" />
                                </div>
                                <div className="cs-caption">{duration}</div>
                              </>
                            ) : null}
                          </div>
                        </Link>

                        <div className="relative z-10 flex h-6 w-[60px] flex-row items-center justify-end">
                          {/* Permanent icons layer - hide on hover */}
                          <div className="absolute top-0 right-0 flex h-full items-center gap-1 transition-opacity duration-200 group-hover:opacity-0">
                            {item?.isBookmarked && (
                              <Bookmark
                                isBookmarkedBool={true}
                                courseEntryUid={courseUid}
                                lessonEntryUid={item?.entryUid}
                              />
                            )}

                            {item?.progress?.isCompleted && (
                              <CompleteIcon className="text-[var(--Icons-Medium)]" />
                            )}

                            {!item?.progress?.isCompleted &&
                              !item.text_lesson &&
                              item?.entryUid === currentLessonUid &&
                              isVideoPlaying && (
                                <button
                                  type="button"
                                  onClick={(e) =>
                                    handlePlayPauseClick(e, item?.entryUid, item?.url)
                                  }
                                  className="flex cursor-pointer items-center justify-center p-1 transition-transform hover:scale-110"
                                  aria-label="Pause video"
                                  title="Pause video"
                                >
                                  <PausePlayerIcon className="text-[var(--Icons-Medium)]" />
                                </button>
                              )}
                          </div>

                          {/* Hover icons layer - absolutely positioned, fades in on hover */}
                          <div className="absolute top-0 right-0 flex h-full items-center gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                            <Bookmark
                              isBookmarkedBool={item?.isBookmarked ?? false}
                              courseEntryUid={courseUid}
                              lessonEntryUid={item?.entryUid}
                            />

                            {item?.progress?.isCompleted && (
                              <CompleteIcon className="text-[var(--Icons-Medium)]" />
                            )}

                            {!item?.progress?.isCompleted &&
                              !item.text_lesson &&
                              item?.entryUid === currentLessonUid &&
                              isVideoPlaying && (
                                <button
                                  type="button"
                                  onClick={(e) =>
                                    handlePlayPauseClick(e, item?.entryUid, item?.url)
                                  }
                                  className="flex cursor-pointer items-center justify-center p-1 transition-transform hover:scale-110"
                                  aria-label="Pause video"
                                  title="Pause video"
                                >
                                  <PausePlayerIcon className="text-[var(--Icons-Medium)]" />
                                </button>
                              )}

                            {!item?.progress?.isCompleted &&
                              !item.text_lesson &&
                              !(item?.entryUid === currentLessonUid && isVideoPlaying) && (
                                <button
                                  type="button"
                                  onClick={(e) =>
                                    handlePlayPauseClick(e, item?.entryUid, item?.url)
                                  }
                                  className="flex cursor-pointer items-center justify-center p-1 transition-transform hover:scale-110"
                                  aria-label="Play video"
                                  title="Play video"
                                >
                                  <PlayVideoIcon className="text-[var(--Icons-Medium)]" />
                                </button>
                              )}
                          </div>
                        </div>
                      </div>

                      {item?.progress?.percentage > 0 && (
                        <div className="absolute bottom-0 left-0 mt-3 flex h-1 w-full bg-[var(--Surfaces-Overlay-Sheen-75)]">
                          <div
                            className={`h-1 transition-all duration-300 ${
                              Number(sanitizeProgress(item?.progress?.percentage ?? 0)) === 100
                                ? 'bg-[var(--Color-Extended-Green-Green-Shade)]'
                                : 'bg-[var(--Surfaces-Extended-Amethyst-Transparent-Light)]'
                            }`}
                            style={{
                              width: `${sanitizeProgress(item?.progress?.percentage ?? 0)}%`,
                            }}
                          ></div>
                        </div>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}
